import React, { useState } from 'react';
import { formRowSx } from '../utils/panelSurfaces';
import PropTypes from 'prop-types';
import {
  Box, Typography, Button, TextField, Select, MenuItem,
  FormControl, Divider
} from '@mui/material';

// The Add-value box holds a multi-digit ordinal (a nakshatra runs to 27, and Group 1's rows
// are seeded with values a user then edits). At 56px a three-digit entry was clipped by the
// number spinner, so the figure being submitted could not be read back. 84 = 56 x 1.5.
const ADD_VALUE_WIDTH = 84;

const PLANETS       = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu', 'Ketu'];
const DECL_ASPECTS  = [
  'Parallel-N-Same Direction',
  'Parallel-N-Opp Direction',
  'Parallel-S-Same Direction',
  'Parallel-S-Opp Direction',
  'Parallel-N-Any Direction',
  'Parallel-S-Any Direction',
  'Parallel',
  'Contra Parallel-Same Direction',
  'Contra Parallel-Opp Direction',
  'Contra Parallel-Any Direction',
  'Zero Crossing - N2S',
  'Zero Crossing - S2N',
];
const SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];

/**
 * The bodies that orbit inside Earth's, and so can pass between Earth and the Sun.
 *
 * Only these have an inferior/superior phase: anything further out than Earth is never nearer to
 * us than the Sun, so the filter would be a constant rather than a choice. Mirrors INNER_PLANETS
 * in NewStudiesService — the backend enforces the same rule, this only keeps the control honest.
 */
const INNER_PLANETS = ['Mercury', 'Venus'];
const hasInnerPlanet = (a, b) => INNER_PLANETS.includes(a) || INNER_PLANETS.includes(b);

/** Sent as the bare value; labelled with what the phase means, since the terms are not obvious. */
const CONJUNCTION_PHASES = [
  { value: 'Any',      label: 'All / Any' },
  { value: 'Inferior', label: 'Inferior (near Earth)' },
  { value: 'Superior', label: 'Superior (far Earth)' },
];

/**
 * The initial state of every row on this tab.
 *
 * Exported because MOAStrategyDialog owns the state and passes it back down, so it needs the same
 * shape — and a second copy over there is a row that exists in the markup but not in the state.
 * Adding r11 with the copy still in place did exactly that: the row rendered with `form` undefined
 * and took the whole dialog down. Same reason ZODIAC_HELIO_INIT lives with its own tab.
 */
export const GROUP2_INIT = {
  r1: { planet: 'Sun', longitude: '', sign: 'Aries',  value: '2' },
  r2: { planet: 'Sun', sign: 'Aries',                 value: '2' },
  r3: { planet: 'Sun', longitude: '',                  value: '2' },
  r4: { harmonic: '30', planet: 'Sun', startingOffset: '0', forPercent: '50', value: '2' },
  r7: { planet: 'Sun',  fromLon: '', toLon: '',        value: '2' },
  r9: { planet: 'Sun',  fromLon: '', toLon: '',        value: '2' },
  r10: { planet1: 'Sun', planet2: 'Moon', declAspect: 'Parallel', value: '2' },
  r11: { phase: 'Any', planet1: 'Mercury', planet2: 'Sun', orb: '3', value: '2' },
  r12: { phase: 'Any', planet1: 'Mercury', planet2: 'Sun', orb: '3', value: '2' },
};

const PLANET_SX = { minWidth: 110 };
const SIGN_SX   = { minWidth: 130 };

const PlanetSelect = ({ value, onChange, exclude }) => (
  <FormControl size="small" sx={PLANET_SX}>
    <Select value={value} onChange={e => onChange(e.target.value)}>
      {PLANETS.filter(p => !exclude || !exclude.includes(p)).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
    </Select>
  </FormControl>
);
PlanetSelect.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, exclude: PropTypes.array };

const SignSelect = ({ value, onChange }) => (
  <FormControl size="small" sx={SIGN_SX}>
    <Select value={value} onChange={e => onChange(e.target.value)}>
      {SIGNS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
    </Select>
  </FormControl>
);
SignSelect.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const Label = ({ children }) => (
  <Typography sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{children}</Typography>
);
Label.propTypes = { children: PropTypes.node.isRequired };

const NumField = ({ value, onChange, width }) => (
  <TextField
    size="small"
    type="number"
    value={value}
    onChange={e => onChange(e.target.value)}
    sx={{ width: width || 80 }}
  />
);
NumField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, width: PropTypes.number };

const LonField = ({ value, onChange }) => (
  <TextField size="small" type="number" value={value}
    onChange={e => onChange(e.target.value)}
    inputProps={{ min: 0, max: 359.99, step: 0.01 }}
    sx={{ width: 80 }} />
);
LonField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const LabeledNumField = ({ label, value, onChange, width }) => (
  <TextField
    label={label}
    size="small"
    type="number"
    value={value}
    onChange={e => onChange(e.target.value)}
    sx={{ width: width || 100 }}
  />
);
LabeledNumField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  width: PropTypes.number,
};

const AddValueField = ({ value, onChange }) => (
  <TextField
    size="small"
    type="number"
    value={value}
    onChange={e => onChange(e.target.value)}
    inputProps={{ min: 1, step: 1 }}
    sx={{ width: ADD_VALUE_WIDTH }}
  />
);
AddValueField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const AddBtn = ({ onClick, disabled }) => (
  <Button
    variant="contained"
    size="small"
    onClick={onClick}
    disabled={disabled}
    sx={{ minWidth: 56, px: 1.5, backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}
  >
    ADD
  </Button>
);
AddBtn.propTypes = { onClick: PropTypes.func.isRequired, disabled: PropTypes.bool };

const CriteriaRow = ({ children }) => (
  <Box sx={(theme) => ({
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1,
    borderRadius: 1, p: 1, mb: 1, ...formRowSx('tinted')(theme)
  })}>
    {children}
  </Box>
);
CriteriaRow.propTypes = { children: PropTypes.node.isRequired };

/**
 * Conjunction of two bodies, optionally filtered to one phase of an inner planet's orbit.
 *
 * <p>One component, rendered once per zodiac section, because the two rows differ only in the
 * frame they name. The frame is carried into the request and cannot change the result — a
 * conjunction is a difference of longitudes and the ayanamsa cancels in it — so the sidereal row
 * is there because the tab is organised by frame and a section missing a row reads as an
 * oversight, not because it answers a different question.
 *
 * <p>The phase control disables itself when neither body is an inner planet, and the state is
 * cleared to 'Any' at the same moment, so what is submitted is always what is displayed.
 */
const ConjunctionPhaseRow = ({ rowKey, frame, form, onFieldChange, onBodyChange, onAdd }) => {
  const innerInvolved = hasInnerPlanet(form.planet1, form.planet2);
  const orb = Number(form.orb);

  return (
    <CriteriaRow>
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <Select
          value={innerInvolved ? form.phase : 'Any'}
          disabled={!innerInvolved}
          inputProps={{ 'aria-label': `${frame} conjunction phase` }}
          onChange={e => onFieldChange(rowKey, 'phase', e.target.value)}
        >
          {CONJUNCTION_PHASES.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
        </Select>
      </FormControl>
      <Label>Conjunction of</Label>
      <PlanetSelect value={form.planet1} onChange={v => onBodyChange(rowKey, 'planet1', v, 'planet2')} exclude={[form.planet2]} />
      <Label>with</Label>
      <PlanetSelect value={form.planet2} onChange={v => onBodyChange(rowKey, 'planet2', v, 'planet1')} exclude={[form.planet1]} />
      <Label>Orb</Label>
      <NumField value={form.orb} onChange={v => onFieldChange(rowKey, 'orb', v)} width={70} />
      <Box sx={{ flex: 1 }} />
      <Label>Add value</Label>
      <AddValueField value={form.value} onChange={v => onFieldChange(rowKey, 'value', v)} />
      <AddBtn
        disabled={!form.orb || !(orb > 0)}
        onClick={() => onAdd('geo_conjunction_phase', {
          planet1: form.planet1,
          planet2: form.planet2,
          // Never the stale state behind a disabled control — see the note above.
          phase: innerInvolved ? form.phase : 'Any',
          orb,
          frame,
          value: form.value,
        })}
      />
    </CriteriaRow>
  );
};
ConjunctionPhaseRow.propTypes = {
  rowKey: PropTypes.string.isRequired,
  frame: PropTypes.oneOf(['tropical', 'sidereal']).isRequired,
  form: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onBodyChange: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

const MOAGroup2Tab = ({ onAddCondition, forms: propForms, onFormsChange }) => {
  const [localForms, setLocalForms] = useState(GROUP2_INIT);
  const forms = propForms || localForms;
  const setForms = onFormsChange || setLocalForms;

  const set = (row, field, val) => setForms(f => ({ ...f, [row]: { ...f[row], [field]: val } }));
  const setPair = (row, f1, v, f2) => setForms(prev => {
    const updated = { ...prev[row], [f1]: v };
    if (updated[f2] === v) updated[f2] = PLANETS.find(p => p !== v) || PLANETS[0];
    return { ...prev, [row]: updated };
  });

  /**
   * Changing one body of a conjunction row, with both guards applied together.
   *
   * Picking the body already in the other dropdown moves that one aside, as setPair does — a
   * body cannot be conjunct itself. And if the new pair has no inner planet, the phase is reset
   * rather than left holding a value the now-disabled control no longer shows: the row would
   * otherwise submit Inferior for, say, Mars with Jupiter, which has no such state.
   */
  const setConjunctionBody = (row, field, v, otherField) => setForms(prev => {
    const updated = { ...prev[row], [field]: v };
    if (updated[otherField] === v) updated[otherField] = PLANETS.find(p => p !== v) || PLANETS[0];
    if (!hasInnerPlanet(updated.planet1, updated.planet2)) updated.phase = 'Any';
    return { ...prev, [row]: updated };
  });

  const add = (type, condObj) => {
    const val = parseInt(condObj.value, 10);
    if (!val || val < 1) return;
    onAddCondition({ group: 2, type, ...condObj, value: val });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography fontWeight="bold" fontSize={15} sx={{ textAlign: 'center', mb: 2, letterSpacing: 0.5 }}>
        Tropical Zodiac Criteria
      </Typography>

      {/* Row 1: Planet At Longitude In Sign */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r1.planet} onChange={v => set('r1', 'planet', v)} />
        <Label>At Longitude</Label>
        <LonField value={forms.r1.longitude} onChange={v => set('r1', 'longitude', v)} />
        <Label>In Sign</Label>
        <SignSelect value={forms.r1.sign} onChange={v => set('r1', 'sign', v)} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r1.value} onChange={v => set('r1', 'value', v)} />
        <AddBtn
          disabled={!forms.r1.longitude}
          onClick={() => add('tropical_at_longitude_in_sign', { planet: forms.r1.planet, longitude: Number(forms.r1.longitude), sign: forms.r1.sign, value: forms.r1.value })}
        />
      </CriteriaRow>

      {/* Row 2: Planet Ingress Rashi/Sign */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r2.planet} onChange={v => set('r2', 'planet', v)} />
        <Label>Ingress Rashi/Sign</Label>
        <SignSelect value={forms.r2.sign} onChange={v => set('r2', 'sign', v)} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r2.value} onChange={v => set('r2', 'value', v)} />
        <AddBtn
          onClick={() => add('tropical_ingress_rashi', { planet: forms.r2.planet, sign: forms.r2.sign, value: forms.r2.value })}
        />
      </CriteriaRow>

      {/* Row 3: Planet At Longitude */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r3.planet} onChange={v => set('r3', 'planet', v)} />
        <Label>At Longitude</Label>
        <LonField value={forms.r3.longitude} onChange={v => set('r3', 'longitude', v)} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r3.value} onChange={v => set('r3', 'value', v)} />
        <AddBtn
          disabled={!forms.r3.longitude}
          onClick={() => add('tropical_at_longitude', { planet: forms.r3.planet, longitude: Number(forms.r3.longitude), value: forms.r3.value })}
        />
      </CriteriaRow>

      {/* Row 4: Harmonic */}
      <CriteriaRow>
        <Label>Harmonic</Label>
        <NumField value={forms.r4.harmonic} onChange={v => set('r4', 'harmonic', v)} width={70} />
        <Label>of planet</Label>
        <PlanetSelect value={forms.r4.planet} onChange={v => set('r4', 'planet', v)} />
        <LabeledNumField
          label="Offset (degrees)"
          value={forms.r4.startingOffset}
          onChange={v => set('r4', 'startingOffset', v)}
          width={120}
        />
        <Label>and for</Label>
        <NumField value={forms.r4.forPercent} onChange={v => set('r4', 'forPercent', v)} width={70} />
        <Label>% of harmonic</Label>
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r4.value} onChange={v => set('r4', 'value', v)} />
        <AddBtn
          disabled={!forms.r4.harmonic}
          onClick={() => add('harmonic', { harmonic: Number(forms.r4.harmonic), planet: forms.r4.planet, startingOffset: Number(forms.r4.startingOffset), forPercent: Number(forms.r4.forPercent), value: forms.r4.value })}
        />
      </CriteriaRow>

      {/* Row 7: Planet from Longitude X To Longitude Y (Tropical) */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r7.planet} onChange={v => set('r7', 'planet', v)} />
        <Label>from Longitude</Label>
        <LonField value={forms.r7.fromLon} onChange={v => set('r7', 'fromLon', v)} />
        <Label>To Longitude</Label>
        <LonField value={forms.r7.toLon} onChange={v => set('r7', 'toLon', v)} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r7.value} onChange={v => set('r7', 'value', v)} />
        <AddBtn
          disabled={!forms.r7.fromLon || !forms.r7.toLon}
          onClick={() => add('tropical_longitude_range', { planet: forms.r7.planet, fromLon: Number(forms.r7.fromLon), toLon: Number(forms.r7.toLon), value: forms.r7.value })}
        />
      </CriteriaRow>

      {/* Row 11: Inferior/Superior geocentric conjunction (Tropical) */}
      <ConjunctionPhaseRow
        rowKey="r11"
        frame="tropical"
        form={forms.r11}
        onFieldChange={set}
        onBodyChange={setConjunctionBody}
        onAdd={add}
      />

      {/* The heliocentric rows that used to sit here now have their own tab, Zodiac-Helio. They
          are a different frame rather than another section of this one — the Sun has no
          heliocentric position, so the two halves could not even offer the same planet list. */}

      {/* ── Sidereal Zodiac Criteria ── */}
      <Box sx={{ mt: 4, mb: 1 }}>
        <Divider />
        <Typography fontWeight="bold" fontSize={15} sx={{ textAlign: 'center', mt: 2, mb: 2, letterSpacing: 0.5 }}>
          Sidereal Zodiac Criteria
        </Typography>
      </Box>

      {/* Row 9: Planet from Longitude X To Longitude Y (Sidereal) */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r9.planet} onChange={v => set('r9', 'planet', v)} />
        <Label>from Longitude</Label>
        <LonField value={forms.r9.fromLon} onChange={v => set('r9', 'fromLon', v)} />
        <Label>To Longitude</Label>
        <LonField value={forms.r9.toLon} onChange={v => set('r9', 'toLon', v)} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r9.value} onChange={v => set('r9', 'value', v)} />
        <AddBtn
          disabled={!forms.r9.fromLon || !forms.r9.toLon}
          onClick={() => add('sidereal_longitude_range', { planet: forms.r9.planet, fromLon: Number(forms.r9.fromLon), toLon: Number(forms.r9.toLon), value: forms.r9.value })}
        />
      </CriteriaRow>

      {/* Row 10: Planet Declination aspect With Planet (Sidereal) */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r10.planet1} onChange={v => setPair('r10', 'planet1', v, 'planet2')} exclude={[forms.r10.planet2]} />
        <Label>Declination aspect</Label>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <Select value={forms.r10.declAspect} onChange={e => set('r10', 'declAspect', e.target.value)}>
            {DECL_ASPECTS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </Select>
        </FormControl>
        <Label>With Planet</Label>
        <PlanetSelect value={forms.r10.planet2} onChange={v => setPair('r10', 'planet2', v, 'planet1')} exclude={[forms.r10.planet1]} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r10.value} onChange={v => set('r10', 'value', v)} />
        <AddBtn
          onClick={() => add('sidereal_decl_aspect', { planet1: forms.r10.planet1, planet2: forms.r10.planet2, declAspect: forms.r10.declAspect, value: forms.r10.value })}
        />
      </CriteriaRow>

      {/* Row 12: Inferior/Superior geocentric conjunction (Sidereal) */}
      <ConjunctionPhaseRow
        rowKey="r12"
        frame="sidereal"
        form={forms.r12}
        onFieldChange={set}
        onBodyChange={setConjunctionBody}
        onAdd={add}
      />

    </Box>
  );
};

MOAGroup2Tab.propTypes = {
  onAddCondition: PropTypes.func.isRequired,
  forms: PropTypes.object,
  onFormsChange: PropTypes.func,
};

export default MOAGroup2Tab;
